<template>
    <v-card elevation="0" class="py-2" tile>

        <v-row no-gutters align="center" justify="center">
            <v-col cols="12" class="text-center">
                <v-icon x-large color="#ffffff">mdi-satellite-variant</v-icon>
            </v-col>
            <v-col cols="12" class="text-center text-lg-h4 text--white80 py-2 pt-6">
                LOCATION PERMISSION
            </v-col>
            <v-col cols="12" class="text-center text--white80">
                {{ permissionText }}
            </v-col>
            <v-col cols="12" class="text-center pt-6">
                <v-btn color="success" @click.stop="requestUserPosition">
                    <v-icon left>mdi-check</v-icon>REQUEST USER POSITION
                </v-btn>
            </v-col>
        </v-row>

        <LoadingDialog
            v-model="isFetchingPosition"
            message="OBTAINING POSITION ..."
        />

        <v-row v-if="showError" no-gutters align="center" justify="center">
            <v-col cols="12" class="text-center pt-2 text--red text-lg-h6">
                ERROR GETTING GEOLOCATION
            </v-col>
        </v-row>
        <v-row v-if="showError" no-gutters align="center" justify="center">
            <v-col cols="12" class="text-center text--red">
                {{ errorMessage }}
            </v-col>
        </v-row>

        <v-row no-gutters align="center" justify="center">
            <v-col cols="12" class="text-center pt-2 text--white80 text-lg-h6">
                {{ instructionHeader }}
            </v-col>
        </v-row>
        <v-row no-gutters align="center" justify="center">
            <v-col v-if="!isDesktop" cols="8" class="text-center">
                <v-img v-if="isIOS"
                       src="@/assets/img/ios-prompt.png"
                       contain>
                </v-img>
                <v-img v-else
                       src="@/assets/img/android-prompt.png"
                       contain>
                </v-img>
            </v-col>
        </v-row>
        <v-row no-gutters align="center" justify="center">
            <v-col cols="12" class="text-center text--white80">
                {{ instructionText }}
            </v-col>
        </v-row>
    </v-card>
</template>

<script lang="ts">
    import {
        defineComponent,
        getCurrentInstance,
        ref,
        computed,
        onMounted
    } from "vue";
    import { alarm, app } from "@/store";
    import {
        canUseGeolocation,
        getGeolocationErrorMessage,
        getGeolocationPermissionState,
        getGeolocationUnavailableMessage,
        isGeolocationSupported,
        MANUAL_GEOLOCATION_OPTIONS,
        requestCurrentPosition,
        resolveGeolocationAccess,
    } from "@/utilities/useGeolocation";
    import useIOSDetection from "@/utilities/useIOSDetection";

    export default defineComponent({
        name: "GeoPermission",
        components: {
            LoadingDialog: () =>
                import("./components/Dialogs/LoadingDialog.vue"),
        },
        props: {
            destination: { type: String, default: "home" },
        },
        setup(props) {
            const root = getCurrentInstance()?.proxy as any;

            const isIOS = useIOSDetection().isIos();
            const isAndroid = /android/i.test(window.navigator.userAgent);
            const isDesktop = !isIOS && !isAndroid;

            const destination = computed<string>(() => props.destination || "home");

            const errorMessage = ref<string>("");
            const isFetchingPosition = ref<boolean>(false);
            const permissionState = ref<string>("unsupported");

            const showError = computed<boolean>(() => {
                return errorMessage.value != "";
            });

            const permissionText = computed<string>(() => {
                if (!isGeolocationSupported()) {
                    return "This browser does not support geolocation.";
                }

                if (!canUseGeolocation()) {
                    return "Location access requires HTTPS or localhost.";
                }

                if (permissionState.value === "denied") {
                    return "Location access is blocked for this site. Enable it in your browser or operating system settings, then try again.";
                }

                if (permissionState.value === "granted") {
                    return "Location access is already allowed. We are retrieving your current position.";
                }

                return "We need your permission to access your location. Click the button below and allow location access when prompted.";
            });

            const instructionHeader = computed<string>(() => {
                if (isDesktop) {
                    return "Desktop Browser Instructions";
                }

                if (isIOS) {
                    return "iOS Instructions (Safari)";
                } else {
                    return "Android Instructions";
                }
            });

            const instructionText = computed<string>(() => {
                if (isDesktop) {
                    return "Use the location icon in the address bar or your browser site settings to allow location access for this site, then return here and try again.";
                }

                if (isIOS) {
                    return "When prompted, click \"Allow\"";
                } else {
                    return "When prompted, select \"Precise\" and then \"While using the app\"";
                }
            });

            const refreshPermissionState = async () => {
                permissionState.value = await getGeolocationPermissionState();
                console.info("[GeoTrace] GeoPermission.refreshPermissionState resolved permission state", {
                    permissionState: permissionState.value,
                    destination: destination.value,
                });
            };

            const requestUserPosition = async () => {
                if (!canUseGeolocation()) {
                    isFetchingPosition.value = false;
                    errorMessage.value = getGeolocationUnavailableMessage();
                    app.error = errorMessage.value;
                    return;
                }

                isFetchingPosition.value = true;

                try {
                    const position = await requestCurrentPosition(MANUAL_GEOLOCATION_OPTIONS);
                    handlePositionSuccess(position);
                } catch (error) {
                    if (error instanceof Error) {
                        isFetchingPosition.value = false;
                        errorMessage.value = error.message || getGeolocationUnavailableMessage();
                        app.error = errorMessage.value;
                        return;
                    }

                    handlePositionError(error as GeolocationPositionError);
                }
            };

            const handlePositionSuccess = (position: GeolocationPosition) => {
                isFetchingPosition.value = false;
                alarm.setUserLocation(position);
                if (destination.value == "mob" && alarm.userLocReady) {
                    root.$router.push({ name: "mob" });
                } else if (destination.value == "map" && alarm.userLocReady) {
                    root.$router.push({ name: "map" });
                } else {
                    root.$router.push({ name: "home" });
                }
            };

            const handlePositionError = (error: GeolocationPositionError) => {
                isFetchingPosition.value = false;
                errorMessage.value = getGeolocationErrorMessage(error);
                app.error = errorMessage.value;
                refreshPermissionState();
            };

            const continueToDestination = () => {
                if (destination.value == "mob") {
                    root.$router.push({ name: "mob" });
                } else if (destination.value == "map") {
                    root.$router.push({ name: "map" });
                } else {
                    root.$router.push({ name: "home" });
                }
            };

            onMounted(async () => {
                await refreshPermissionState();

                const accessResolution = await resolveGeolocationAccess();
                console.info("[GeoTrace] GeoPermission.onMounted effective access", {
                    permissionState: accessResolution.permissionState,
                    accessStatus: accessResolution.accessStatus,
                    destination: destination.value,
                });

                if (accessResolution.accessStatus === "granted") {
                    if (alarm.userLocReady) {
                        continueToDestination();
                        return;
                    }

                    requestUserPosition();
                }
            });

            return {
                requestUserPosition,
                errorMessage,
                instructionHeader,
                instructionText,
                permissionText,
                showError,
                isIOS,
                isDesktop,
                isFetchingPosition,
            };
        },
    });
</script>
<style lang="scss" scoped>
</style>